import { Canceler } from 'axios';
import { action, computed, makeObservable, observable } from 'mobx';
import { defaultExclusion, defaultExclusionValidation, defaultExclusionsFilters } from '../constants/exclusions';
import { IExclusion, IExclusionValidation, IExclusionsFilters } from '../types/exclusions';
import { ITEM_TYPE, LOCALES, defaultPagination } from 'src/constants/main';
import { SCREENS } from 'src/constants/screens';
import client from 'src/services/client';
import { history } from 'src/services/history';
import { ICountsResponce, IPagination, TBooleanInNumber } from 'src/types/main';
import { booleanNumberReverse, countsUpdate, getAvailabilityParams, getCancelToken, getTranslate } from 'src/utils/helpers';
import { emptyNameValidation, sameNameValidation } from 'src/utils/validation';
import { localeStore } from '~/mobx/localesStore';
import logger from '~/services/logger';
import { toastSuccess } from '~/utils/toasts';

class ExclusionsStore {
  constructor() {
    makeObservable(this);
  }

  cancelStack: {
    [key: string]: Canceler;
  } = {};

  @observable pagination: IPagination<IExclusion> = defaultPagination;
  @observable exclusion: IExclusion | null = null;
  @observable edited: boolean = false;
  @observable filters: IExclusionsFilters = defaultExclusionsFilters;
  @action setFilters = (filters: Partial<IExclusionsFilters>) => {
    this.filters = { ...this.filters, ...filters };
    this.fetchExclusions(1);
  };

  @computed get isLoading(): boolean {
    return Boolean(this.pagination.loading || this.pagination.refreshing);
  }

  @action fetchExclusions = async (page: number = this.pagination.current_page + 1) => {
    try {
      const cancelId = 'fetchExclusions';
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      const params: any = {
        page,
        name: this.filters.search,
        is_delete: 0,
        sort_field: this.filters.sort_field,
        sort_dir: this.filters.sort_dir,
        ...getAvailabilityParams(this.filters.availability),
      };

      this.pagination.loading = true;
      this.pagination.refreshing = true;
      const pagination = await client.get('v3/exclusions', {
        params,
        headers: { 'Accept-Language': 'en,ar' },
        cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)),
      });

      this.pagination = {
        ...pagination,
        loading: false,
        refreshing: false,
        data: pagination.data,
      };
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination = {
        ...this.pagination,
        loading: false,
        refreshing: false,
      };
      logger.error({ error, place: 'ExclusionsStore fetchExclusions' });
    }
  };

  public exclusionsCount: number | undefined = undefined;
  public fetchExclusionsCount = async () => {
    try {
      const counts: ICountsResponce = await client.get(`v3/exclusions_count`);
      this.exclusionsCount = counts.available;
    } catch (error: any) {
      logger.error({ error, place: 'ExclusionsStore fetchExclusionsCount' });
    }
  };

  @action getExclusion = async (id: string | number) => {
    try {
      this.pagination.loading = true;
      const exclusion: IExclusion = await client.get(`v3/exclusions/${id}`, { headers: { 'Accept-Language': 'en,ar' } });
      this.setExclusion(exclusion && exclusion.id ? this.transformExclusion(exclusion) : undefined);
      this.pagination.loading = false;
    } catch (error: any) {
      this.pagination.loading = false;
      logger.error({ error, place: 'ExclusionsStore getExclusion', showModal: true });
    }
  };

  @action setExclusion = (exclusion?: IExclusion) => {
    this.exclusion = exclusion ? this.transformExclusion(exclusion) : this.defaultExclusion;
  };
  private get defaultExclusion(): IExclusion {
    const count: string = String((this.exclusionsCount ?? this.pagination.total) + 1);

    return {
      ...defaultExclusion,
      name: {
        en: getTranslate('exclusion', LOCALES.EN) + ' ' + count,
        ar: getTranslate('exclusion', LOCALES.AR) + ' ' + count,
      },
    };
  }
  private transformExclusion = (exclusion: IExclusion): IExclusion => {
    return { ...exclusion, external_id: exclusion.external_id?.toString() };
  };

  @action changeExclusionName = (value: string, currentLanguage: LOCALES) => {
    if (!this.exclusion) return;
    this.exclusion.name = { ...this.exclusion.name, [currentLanguage]: value };
    this.edited = true;
  };

  @action changeExclusionExternalId = (value: string | number) => {
    if (!this.exclusion) return;
    this.exclusion.external_id = value.toString();
    this.edited = true;
  };

  @action changeExclusionAvailability = (status: TBooleanInNumber) => {
    if (!this.exclusion) return;
    this.exclusion.status = status;
    this.edited = true;
  };

  @action createExclusion = async () => {
    try {
      if (!this.exclusion) return;

      this.pagination.loading = true;

      await client.post(`v3/exclusions`, this.params);

      this.edited = false;
      history.push(SCREENS.EXCLUSIONS());

      toastSuccess({ message: localeStore.t('toast_exclusion_changes_saved') });
    } catch (error: any) {
      this.pagination.loading = false;

      // name validation
      [this.validation.name, this.validation.valid] = sameNameValidation({ name: this.validation.name, type: ITEM_TYPE.EXCLUSION, error });
      if (!this.validation.valid) return;

      logger.error({ error, place: 'ExclusionsStore createExclusion', showModal: true });
    }
  };

  @action updateExclusion = async (id: number) => {
    try {
      if (!this.exclusion) return;

      this.pagination.loading = true;

      await client.put(`v3/exclusions/${id}`, this.params);

      this.edited = false;
      history.push(SCREENS.EXCLUSIONS());

      toastSuccess({ message: localeStore.t('toast_exclusion_changes_saved') });
    } catch (error: any) {
      this.pagination.loading = false;

      // name validation
      [this.validation.name, this.validation.valid] = sameNameValidation({ name: this.validation.name, type: ITEM_TYPE.EXCLUSION, error });
      if (!this.validation.valid) return;

      logger.error({ error, place: 'ExclusionsStore updateExclusion', showModal: true });
    }
  };

  private get params() {
    if (!this.exclusion) return {};

    return {
      ...this.exclusion,
      id: this.exclusion.id || undefined,
      external_id: this.exclusion.external_id || null,
      is_draft: undefined,
    };
  }

  @action changeStatus = async (id: number, status: TBooleanInNumber) => {
    try {
      const cancelId = `status_${id}`;
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      this.pagination.data = this.pagination.data.map((exclusion) => (exclusion.id === id ? { ...exclusion, status } : exclusion));

      await client.put(`v3/exclusions/${id}`, { status }, { cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)) });

      toastSuccess({ message: localeStore.t('toast_exclusion_changes_saved') });
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination.data = this.pagination.data.map((exclusion) =>
        exclusion.id === id ? { ...exclusion, status: booleanNumberReverse(exclusion.status) } : exclusion
      );
      logger.error({ error, place: 'ExclusionsStore changeStatus' });
    }
  };

  @action deleteExclusion = async (exclusionId: number) => {
    try {
      const cancelId = `delete_${exclusionId}`;
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      this.pagination.refreshing = true;

      await client.delete(`v3/exclusions/${exclusionId}`, { cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)) });

      this.fetchExclusions(this.pagination.current_page);
      this.exclusionsCount = countsUpdate(this.exclusionsCount);

      toastSuccess({ message: localeStore.t('toast_exclusion_deleted') });
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination.refreshing = false;
      logger.error({ error, place: 'ExclusionsStore deleteExclusion', showModal: true });
    }
  };

  @observable validation: IExclusionValidation = defaultExclusionValidation;
  @action setValidation = () => {
    if (!this.exclusion) return;

    this.validation = { ...defaultExclusionValidation };

    // name validation
    [this.validation.name, this.validation.valid] = emptyNameValidation(this.exclusion.name, this.validation.name, this.validation.valid);
  };

  @action unMount = () => {
    this.exclusion = null;
    this.edited = false;
    this.validation = defaultExclusionValidation;

    if (!window.location.pathname.includes(SCREENS.EXCLUSIONS())) this.filters = defaultExclusionsFilters;
  };
}

const exclusionsStore = new ExclusionsStore();

export default exclusionsStore;
